import numeral from 'numeral'
import type { RoundingFunction } from 'numeral'

import { CurrencyCodes } from '../constants/Numbers'
import { getLocale } from './Localization'

export const generateEqualIntervals = (
  minValue: number,
  maxValue: number,
  tickCount: number,
): number[] => {
  const ticks: number[] = []
  const range = maxValue - minValue
  const step = range / (tickCount - 1) // The space between each tick

  for (let i = 0; i < tickCount; i++) {
    const tickValue = minValue + i * step
    ticks.push(tickValue)
  }

  ticks[tickCount - 1] = maxValue

  return ticks.map(Math.round)
}

// enhances numeral's localization functionality by puting the $ in the correct place
export const formatCurrency = (
  amount: number | undefined,
  formatting: string,
  roundingFunction?: RoundingFunction,
) => {
  switch (getLocale()) {
    case 'fr-ca': {
      return numeral(amount).format(`${formatting} $`, roundingFunction)
    }
    default: {
      return numeral(amount).format(`$${formatting}`, roundingFunction)
    }
  }
}

export const getCurrencyCodes = () => {
  switch (getLocale()) {
    case 'fr-ca':
      return CurrencyCodes.CAD
    default:
      return CurrencyCodes.USD
  }
}

// will return `true` if value is a string number, like '99', which matches Serenity logic
export const isValidNumber = (value: any) =>
  value !== undefined && value !== null && value !== '' && isFinite(value)

const defaultPercentStyle = {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as Intl.NumberFormatOptions

export const formatPercentage = (
  amount: number | string | undefined,
  options: Intl.NumberFormatOptions = defaultPercentStyle,
) =>
  isValidNumber(amount)
    ? new Intl.NumberFormat(getLocale(), options).format(Number(amount))
    : amount

export const getPluralRules = (options: Intl.PluralRulesOptions) =>
  new Intl.PluralRules(getLocale(), options)

export const formatOrdinal = (
  number: number,
  options: Intl.PluralRulesOptions = { type: 'ordinal' },
  copy = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
  } as const,
) => {
  const rule = getPluralRules(options).select(number)

  return `${number}${copy[rule as keyof typeof copy]}`
}
